<template>
  <div class="detalles-del-plan-page">
    <!-- Hero Section - Imagen 1.jpg con copy de marketing -->
    <section
      class="hero-section hero-animate"
      :style="{ backgroundImage: `url(${heroImage})` }"
    >
      <div class="container">
        <div class="row">
          <div class="col-lg-8 col-md-10">
            <h1 class="hero-title">
              Planes que protegen lo que más importa: tu salud y la de tu familia
            </h1>
            <p class="hero-subtitle">
              Accede a consultas médicas ilimitadas, telemedicina 24/7, descuentos en laboratorios y medicamentos. 
              Sin copagos, sin complicaciones. Miles de familias ya cuidan su bienestar con nosotros.
            </p>
            <a href="#nuestros-planes" class="btn btn-hero-cta" @click.prevent="scrollToId('nuestros-planes')">
              Conoce tu plan ideal
              <i class="fas fa-arrow-right ml-2"></i>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Planes destacados: precio real y ahorro del catálogo 2027 -->
    <section v-if="highlightPlans.length" class="plans-carousel-section">
      <div class="container">
        <div class="row align-items-stretch justify-content-center">
          <div
            v-for="(item, index) in highlightPlans"
            :key="item.catalog.id"
            class="col-lg-4 col-md-6 mb-4 d-flex"
          >
            <div class="plan-image-card ddp-visible fade-in-up w-100" :style="`transition-delay: ${index * 0.08}s`">
              <div class="plan-image-top">
                <img :src="item.image" :alt="`Plan ${item.catalog.name}`" class="plan-image" />
                <div class="plan-image-overlay">
                  <a :href="`#plan-${item.catalog.id}`" class="btn btn-lo-necesito" @click.prevent="scrollToId(`plan-${item.catalog.id}`)">
                    Lo necesito ahora <i class="fas fa-arrow-right ml-1"></i>
                  </a>
                </div>
              </div>
              <div class="plan-card-body">
                <h3 class="plan-name">{{ item.catalog.name }}</h3>
                <div class="plan-price-large">
                  <small v-if="item.catalog.fam" class="plan-price-from">Desde</small>${{ item.offer.amount }}<span class="plan-price-suffix">/mes</span>
                </div>
                <div v-if="item.offer.was" class="plan-savings-line">
                  <s class="plan-original">${{ item.offer.was }}</s>
                  <span class="plan-percent">Ahorras {{ item.offer.savingsPercent }}%</span>
                </div>
                <div v-else-if="item.catalog.fam" class="plan-savings-line">
                  <span class="plan-percent">${{ item.offer.perPerson }} por persona · {{ item.offer.people }} personas</span>
                </div>
                <p class="plan-card-for">{{ item.catalog.for }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ¿Por qué elegir? + Cuidado Integral y Preventivo -->
    <section class="cuidado-integral-section">
      <div class="container">
        <div class="section-header text-center mb-5 ddp-visible fade-in-up">
          <h2 class="section-title-main">¿Por qué elegir Hispano Medical Plan?</h2>
          <p class="section-subtitle-main">
            Porque tu salud merece lo mejor. Somos tu aliado de confianza, ofreciendo servicios médicos 
            de excelencia con la simplicidad y accesibilidad que buscas.
          </p>
        </div>
        <div class="cuidado-integral-block ddp-visible fade-in-down" style="transition-delay: 0.1s">
          <div class="row align-items-center">
            <div class="col-lg-8 col-md-7">
              <h3 class="cuidado-block-title">Cuidado Integral y Preventivo</h3>
              <p class="cuidado-block-text">
                Tu bienestar empieza con la prevención. Incluimos evaluaciones médicas completas, 
                chequeos preventivos y seguimiento continuo para que detectes cualquier situación a tiempo. 
                Cuidamos de ti hoy para que disfrutes una vida más saludable mañana.
              </p>
            </div>
            <div class="col-lg-4 col-md-5 text-center">
              <div class="cuidado-icon-wrapper">
                <img :src="cuidadoIcon" alt="Cuidado Integral y Preventivo" class="cuidado-icon" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ¿Por qué elegir Hispano Medical Plan? - Cards en columna izquierda, imagen derecha -->
    <section class="why-choose-section">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-7 col-md-6 order-2 order-lg-1">
            <div class="why-choose-cards-column">
              <div class="why-choose-card ddp-visible fade-in-up" v-for="(card, idx) in whyChooseCards" :key="idx" :style="`transition-delay: ${0.08 + idx * 0.06}s`">
                <div class="card-icon">
                  <img :src="card.icon" :alt="card.title" />
                </div>
                <h4 class="card-title">{{ card.title }}</h4>
                <p class="card-text">{{ card.text }}</p>
              </div>
            </div>
          </div>
          <div class="col-lg-5 col-md-6 order-1 order-lg-2 mb-4 mb-lg-0">
            <div class="why-choose-image-wrapper ddp-visible fade-in-left">
              <img :src="whyChooseImage" alt="Hispano Medical Plan" class="why-choose-image" />
              <router-link to="/plans" class="btn btn-ver-planes-overlay">
                Ver planes disponibles
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Nuestros planes: precio dinámico, forma de pago y ahorro -->
    <section id="nuestros-planes" class="nuestros-planes-section">
      <div class="nuestros-planes-header">
        <div class="container">
          <div class="section-header text-center ddp-visible fade-in-up">
            <h2 class="nuestros-planes-title">Nuestros planes</h2>
            <p class="nuestros-planes-subtitle">
              Elige cómo pagar y mira cuánto ahorras con cada plan
            </p>
          </div>
        </div>
      </div>
      <div class="container">
        <div v-if="loading" class="plans-state" role="status">
          <div class="spinner-border text-primary" aria-hidden="true"></div>
          <p>Cargando planes…</p>
        </div>
        <div v-else-if="!planItems.length" class="plans-state" role="alert">
          <p>No pudimos cargar los planes en este momento.</p>
          <button type="button" class="btn btn-hero-cta" @click="loadPlans">Intentar de nuevo</button>
        </div>
        <template v-else>
          <PlanDetailBlock
            v-for="(item, index) in planItems"
            :key="item.catalog.id"
            :catalog="item.catalog"
            :api-plan="item.api"
            :image="detailImage(item.catalog.id)"
            :reverse="index % 2 === 1"
          />
        </template>
      </div>
    </section>

    <!-- Tabla comparativa -->
    <section v-if="planItems.length" class="comparison-section">
      <div class="container">
        <div class="comparison-header ddp-visible fade-in-up">
          <div class="comparison-header-inner">
            <img :src="comparisonLogo" alt="" class="comparison-logo" />
            <div class="comparison-header-text">
              <h2 class="comparison-title">Comparación de planes</h2>
              <p class="comparison-subtitle">
                Lo que incluye cada plan, lado a lado
              </p>
            </div>
          </div>
        </div>
        <PlanComparisonTable :plans="planItems" @see-plan="(id) => scrollToId(`plan-${id}`)" />
      </div>
    </section>

    <!-- Lo que dicen de nosotros -->
    <TestimonialsSection :testimonials="testimonials" :locations="locations" />

  </div>
</template>

<script>
import plansService from '@/core/services/api/landings/plans'
import { TestimonialsSection } from '../components'
import { HMP_PLANS_2027, matchApiPlan } from '../data/hmpPlans2027'
import { planOffer } from '../data/planOffer'
import PlanDetailBlock from './components/PlanDetailBlock.vue'
import PlanComparisonTable from './components/PlanComparisonTable.vue'

/** Las fotos 1-3 llevan el logo y son las de los planes destacados. */
const HIGHLIGHT_IMAGES = {
  tusalud: require('@/assets/lp/plans/1.png'),
  familiar: require('@/assets/lp/plans/2.png'),
  senior: require('@/assets/lp/plans/3.png')
}

/** Fotografías de los bloques de detalle de cada plan. */
const DETAIL_IMAGES = {
  tusalud: require('@/assets/lp/plans/5.png'),
  senior: require('@/assets/lp/plans/6.png'),
  plus: require('@/assets/lp/plans/4.png'),
  familiar: require('@/assets/lp/plans/7.png'),
  empresas: require('@/assets/lp/plans/8.png')
}

const HIGHLIGHT_FAMILY_PEOPLE = 2

export default {
  name: 'DetallesDelPlan',
  components: {
    TestimonialsSection,
    PlanDetailBlock,
    PlanComparisonTable
  },
  data() {
    return {
      plans: [],
      loading: true,
      heroImage: require('@/assets/lp/plans/1.jpg'),
      cuidadoIcon: require('@/assets/lp/plans/icons/Recurso 39@300x.png'),
      comparisonLogo: require('@/assets/images/logoLp.png'),
      whyChooseImage: require('@/assets/lp/2.png'),
      whyChooseCards: [
        {
          icon: require('@/assets/lp/Recurso 37@300x.png'),
          title: 'Red de Clínicas Afiliadas',
          text: 'Más de 50 clínicas esperándote. Donde estés, tenemos un profesional listo para cuidarte. Tu salud, a la vuelta de la esquina.'
        },
        {
          icon: require('@/assets/lp/Recurso 38@300x.png'),
          title: 'Ahorro en Medicamentos',
          text: 'Con ahorros de hasta 80%, recuperas el costo de tu plan en solo 2 meses. Tus medicamentos te esperan en tu clínica, sin filas.'
        },
        {
          icon: require('@/assets/lp/Recurso 39@300x.png'),
          title: 'Consultas por Video',
          text: 'Consulta con doctores certificados en minutos, desde tu celular. Sin citas, sin esperas. Tu salud, en tus manos, 24/7.'
        }
      ],
      testimonials: [
        { text: 'Gracias a Hispano Medical Plan, he podido llevar a mis hijos al médico sin preocuparme por los costos. El personal es amable y siempre nos atienden en español.', author: 'María Elena Martínez' },
        { text: 'Los descuentos en medicamentos han sido una gran ayuda para mi tratamiento. Ahorro hasta un 75% en mis medicamentos mensuales. Excelente servicio.', author: 'Carlos Rodríguez' },
        { text: 'La telemedicina ha cambiado mi vida. Puedo hablar con un médico a cualquier hora sin salir de casa. Recomiendo Hispano Medical Plan a toda mi familia.', author: 'Ana García' }
      ],
      locations: [
        { name: 'Oak Cliff', address: '3434 W Illinois Ave Ste 202, Dallas, Texas, 75211', phone: '+12148889000' },
        { name: 'First Street Garland', address: '3050 S. First St, Garland, Texas, 75041', phone: '+12148889000' },
        { name: 'Fort Worth', address: '1303 N Beach St, Fort Worth, Texas, 76111', phone: '+12148889000' },
        { name: 'Garland', address: '3516 W Walnut St, Garland, Texas, 75042', phone: '+12148889000' },
        { name: 'Buckner Blvd Dallas', address: '3045 N Buckner Blvd Suite 110, Dallas, Texas, 75228', phone: '+12148889000' },
        { name: 'Coit', address: '14211 Coit Rd, Dallas, Texas, 75254', phone: '+12148889000' },
        { name: 'Denton', address: '231 W University Dr #111, Denton, Texas, 76201', phone: '+12148889000' },
        { name: 'Irving', address: '204 S Nursery Rd #166, Irving, Texas, 75060', phone: '+12148889000' },
        { name: 'Josey Lane', address: '3065 N Josey Ln Apt 28, Carrollton, Texas, 75007', phone: '+12148889000' },
        { name: 'McKinney', address: '811 N McDonald St, McKinney, Texas, 75069', phone: '+12148889000' }
      ]
    }
  },
  computed: {
    planItems() {
      return HMP_PLANS_2027
        .map((catalog) => ({ catalog, api: matchApiPlan(catalog, this.plans) }))
        .filter((item) => item.api)
    },
    highlightPlans() {
      return this.planItems
        .filter((item) => HIGHLIGHT_IMAGES[item.catalog.id])
        .map((item) => ({
          ...item,
          image: HIGHLIGHT_IMAGES[item.catalog.id],
          offer: planOffer(item.catalog, item.api, { interval: 'month', people: HIGHLIGHT_FAMILY_PEOPLE })
        }))
    }
  },
  mounted() {
    this.loadPlans()
    this.setupScrollAnimations()
  },
  methods: {
    async loadPlans() {
      this.loading = true
      try {
        const response = await plansService.getAllPlans()
        let plansData = []
        if (response && response.status === 'ok' && response.data && Array.isArray(response.data)) {
          plansData = response.data
        } else if (Array.isArray(response)) {
          plansData = response
        } else if (response && response.data && Array.isArray(response.data)) {
          plansData = response.data
        }
        this.plans = plansData
      } catch (error) {
        console.error('Error al cargar planes:', error)
        this.plans = []
      } finally {
        this.loading = false
      }
    },
    detailImage(catalogId) {
      return DETAIL_IMAGES[catalogId] || null
    },
    scrollToId(id) {
      const target = document.getElementById(id)
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
        setupScrollAnimations() {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible')
            }
          })
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      )
      const markVisibleIfInViewport = () => {
        const ddp = this.$el.querySelectorAll('.ddp-visible')
        const animated = this.$el.querySelectorAll('.animate-on-scroll')
        const elements = [...ddp, ...animated]
        elements.forEach(el => {
          observer.observe(el)
          const rect = el.getBoundingClientRect()
          const isInViewport = rect.top < window.innerHeight * 0.9 && rect.bottom > 0
          if (isInViewport) {
            setTimeout(() => el.classList.add('is-visible'), 100)
          }
        })
      }
      this.$nextTick(markVisibleIfInViewport)
      // Revisar de nuevo cuando termine el loading (algunas secciones se renderizan después)
      this.$watch('loading', (val) => {
        if (!val) this.$nextTick(markVisibleIfInViewport)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.detalles-del-plan-page {
  overflow-x: hidden;
}

/* Hero - sin overlay, imagen ya trae fondo azul */
.hero-section {
  min-height: 60vh;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 80px 0;
  color: white;
  display: flex;
  align-items: center;

  .hero-title {
    font-size: clamp(2.5rem, 6.5vw, 4.5rem);
    font-weight: 700;
    margin-bottom: 1.5rem;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    line-height: 1.2;
  }

  .hero-subtitle {
    font-size: clamp(1.15rem, 2.5vw, 1.75rem);
    margin-bottom: 2rem;
    opacity: 0.95;
    line-height: 1.6;
  }

  .btn-hero-cta {
    background: $landing-secondary-color;
    color: white;
    padding: 14px 32px;
    border-radius: 8px;
    font-weight: 700;
    font-size: 1.5rem;
    text-transform: uppercase;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    border: none;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(34, 207, 224, 0.4);
      background: darken($landing-secondary-color, 8%);
      color: white;
      text-decoration: none;
    }
  }

  @media (max-width: 768px) {
    min-height: 50vh;
    padding: 60px 1rem;
  }
}

/* Sección 3 planes */
.plans-carousel-section {
  padding: 80px 0;
  background: $landing-bg-light;

  .plan-image-card {
    background: transparent;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: none;
    transition: all 0.3s ease;
    height: 100%;
    display: flex;
    flex-direction: column;

    &:hover {
      transform: translateY(-4px);
    }

    .plan-image-top {
      position: relative;
      aspect-ratio: 3/4;
      overflow: hidden;
      border-radius: 15px 15px 0 0;

      .plan-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .plan-image-overlay {
        position: absolute;
        bottom: 1rem;
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
      }

      .btn-lo-necesito {
        background: white;
        color: $landing-primary-color;
        border: 2px solid $landing-primary-color;
        padding: 12px 24px;
        border-radius: 8px;
        font-weight: 600;
        font-size: 1.15rem;
        text-transform: uppercase;
        text-decoration: none;
        transition: all 0.3s ease;

        &:hover {
          background: $landing-primary-color;
          color: white;
          text-decoration: none;
        }
      }
    }

    .plan-card-body {
      padding: 1.25rem 1.5rem;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      flex: 1;
      min-height: 0;
    }

    .plan-name {
      font-size: 1.75rem;
      font-weight: 600;
      color: $landing-text-dark;
      margin: 0 0 0.5rem;
    }

    .plan-price-large {
      font-size: 3.6rem;
      font-weight: 700;
      line-height: 1.1;
      color: $landing-primary-color;
      margin-bottom: 0.5rem;
      font-variant-numeric: tabular-nums;

      .plan-price-from {
        display: block;
        font-size: 1rem;
        font-weight: 600;
        color: $landing-text-medium;
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }

      .plan-price-suffix {
        margin-left: 4px;
        font-size: 1.2rem;
        font-weight: 500;
        color: $landing-text-medium;
      }
    }

    .plan-savings-line {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      gap: 0.6rem;
      margin-bottom: 0.9rem;

      .plan-original {
        color: $landing-text-medium;
        font-size: 1.2rem;
      }

      .plan-percent {
        padding: 4px 12px;
        border-radius: 999px;
        background: rgba(34, 207, 224, 0.14);
        color: $landing-accent-text;
        font-size: 0.95rem;
        font-weight: 600;
      }
    }

    .plan-card-for {
      margin: 0;
      color: $landing-text-medium;
      font-size: 1rem;
      line-height: 1.5;
      max-width: 22em;
    }
  }
}

/* ¿Por qué elegir? + Cuidado Integral */
.cuidado-integral-section {
  padding: 80px 0;
  background: white;

  .section-title-main {
    font-size: clamp(2.2rem, 5.5vw, 3.5rem);
    font-weight: 700;
    color: $landing-primary-color;
    margin-bottom: 1rem;
  }

  .section-subtitle-main {
    font-size: 1.6rem;
    color: $landing-text-medium;
    line-height: 1.7;
    margin: 0;
    max-width: 750px;
    margin-left: auto;
    margin-right: auto;
  }

  .cuidado-integral-block {
    background: $landing-primary-color;
    border-radius: 15px;
    padding: 3rem 2.5rem;
  }

  .cuidado-block-title {
    font-size: clamp(2rem, 4vw, 3.2rem);
    font-weight: 700;
    color: white;
    margin-bottom: 1rem;
  }

  .cuidado-block-text {
    font-size: 1.5rem;
    color: white;
    line-height: 1.8;
    margin: 0;
    opacity: 0.95;
  }

  .cuidado-icon-wrapper {
    .cuidado-icon {
      max-width: 200px;
      height: auto;
    }
  }
}

/* ¿Por qué elegir? - igual que Home benefits-section */
.why-choose-section {
  padding: 100px 0;
  background: linear-gradient(180deg, #F3F6F9 0%, #ffffff 50%, #F3F6F9 100%);

  .why-choose-image-wrapper {
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    .why-choose-image {
      width: 100%;
      height: auto;
      object-fit: contain;
    }

    .btn-ver-planes-overlay {
      position: absolute;
      bottom: 1.5rem;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, $landing-secondary-color 0%, $landing-secondary-color 65%, $landing-primary-color 100%);
      color: white;
      border: none;
      padding: 16px 48px;
      min-width: 280px;
      border-radius: 10px;
      font-weight: 700;
      font-size: 1.5rem;
      text-transform: uppercase;
      text-align: center;
      text-decoration: none;
      transition: all 0.3s ease;

      &:hover {
        background: linear-gradient(135deg, darken($landing-secondary-color, 5%) 0%, darken($landing-secondary-color, 5%) 65%, $landing-primary-color 100%);
        color: white;
        text-decoration: none;
        transform: translate(-50%, -2px);
      }
    }
  }

  .why-choose-cards-column {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .why-choose-card {
    background: white;
    border-radius: 15px;
    padding: 2.5rem;
    border: 2px solid $landing-border-light;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);

      .card-icon img {
        transform: scale(1.05);
      }
    }

    .card-icon {
      width: 80px;
      height: 80px;
      background: transparent;
      border: none;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 1.5rem;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        transition: transform 0.3s ease;
      }
    }

    .card-title {
      font-size: 1.75rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin: 0 0 1.25rem;
      line-height: 1.3;
      width: 100%;
    }

    .card-text {
      font-size: 1.1rem;
      color: $landing-text-dark;
      line-height: 1.7;
      margin: 0;
      font-weight: 400;
      width: 100%;
    }
  }
}

/* Nuestros planes */
.nuestros-planes-section {
  padding: 0 0 80px;
  background: white;

  .nuestros-planes-header {
    background: $landing-primary-color;
    padding: 4rem 0;
    margin-bottom: 3rem;
  }

  .nuestros-planes-title {
    font-size: clamp(2.5rem, 6vw, 4.5rem);
    font-weight: 700;
    color: white;
    margin: 0 0 1rem;
    line-height: 1.2;
  }

  .nuestros-planes-subtitle {
    font-size: clamp(1.4rem, 3vw, 2rem);
    color: white;
    margin: 0;
    line-height: 1.5;
    opacity: 0.95;
  }

  .plans-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 4rem 0;
    color: $landing-text-medium;
    font-size: 1.1rem;
    text-align: center;
  }
}

#nuestros-planes {
  scroll-margin-top: 90px;
}

.comparison-section {
  padding: 80px 0;
  background: white;

  .comparison-header {
    margin-bottom: 4rem;

    .comparison-header-inner {
      display: flex;
      align-items: center;
      gap: 1.25rem;
      flex-wrap: wrap;
      justify-content: center;
    }

    .comparison-logo {
      height: 56px;
      width: auto;
      object-fit: contain;
    }

    .comparison-title {
      font-size: clamp(2rem, 5vw, 3rem);
      font-weight: 700;
      color: $landing-primary-color;
      margin: 0 0 0.35rem;
      line-height: 1.2;
      letter-spacing: 0.08em;
    }

    .comparison-subtitle {
      font-size: 1.15rem;
      color: $landing-text-medium;
      margin: 0;
      letter-spacing: 0.02em;
    }
  }

}

/* Animaciones sutiles de subida/bajada al hacer scroll */
.ddp-visible {
  opacity: 0;
  transition: opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  &.fade-in-up {
    transform: translateY(18px);

    &.is-visible {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &.fade-in-down {
    transform: translateY(-18px);

    &.is-visible {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &.fade-in-left {
    transform: translateX(-18px);

    &.is-visible {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &.is-visible {
    opacity: 1;
    transform: translate(0);
  }
}

@media (max-width: 991px) {
  .plans-carousel-section,
  .cuidado-integral-section,
  .why-choose-section,
  .nuestros-planes-section,
  .comparison-section {
    padding: 60px 0;
  }
}

@media (max-width: 768px) {
  .hero-section {
    .hero-title {
      font-size: 2rem;
    }

    .hero-subtitle {
      font-size: 1.15rem;
    }
  }

  .plans-carousel-section .plan-image-card .plan-name {
    font-size: 1.5rem;
  }
}
</style>
